import React from 'react';
import { Plus, Minus, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { sameName } from './useCustomOrderForm';

const ProductRow = ({ product, onAdd }) => (
  <div
    className="flex items-center justify-between px-3 py-2 hover:bg-gray-50 border-b last:border-b-0 cursor-pointer transition-colors"
    onClick={() => onAdd(product)}
    data-testid={`custom-order-add-product-${product.id}`}
  >
    <div className="min-w-0">
      <span className="font-medium text-sm">{product.name}</span>
      {product.pack_size && <span className="text-xs text-gray-500 ml-2">({product.pack_size})</span>}
    </div>
    <div className="flex items-center gap-2 shrink-0">
      <span className="text-sm font-medium">${(Number(product.price) || 0).toFixed(2)}</span>
      <Plus className="h-4 w-4 text-amber-600" />
    </div>
  </div>
);

// The browsable catalog, exactly as the regular New Order form presents it,
// with one explicit action for a line the catalog does not have.
export const CustomOrderProducts = ({ products, items, loading, onAdd, onAddOneOff }) => {
  const picked = new Set(items.map((i) => i.product_id).filter(Boolean));
  const available = products.filter((p) => !picked.has(p.id));
  return (
    <div>
      <Label className="text-base font-semibold">Products</Label>
      {loading ? (
        <p className="text-sm text-gray-500 py-2">Loading products...</p>
      ) : available.length === 0 ? (
        <p className="text-sm text-gray-500 py-2" data-testid="custom-order-no-products">
          {products.length === 0 ? 'No active products available.' : 'Every product is already on this order.'}
        </p>
      ) : (
        <div className="border rounded-lg mt-2 max-h-48 overflow-y-auto" data-testid="custom-order-product-list">
          {available.map((p) => <ProductRow key={p.id} product={p} onAdd={onAdd} />)}
        </div>
      )}
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="mt-3 min-h-[40px]"
        onClick={onAddOneOff}
        data-testid="custom-order-add-one-off"
      >
        <Plus className="h-4 w-4 mr-1" /> Add a one-off item
      </Button>
      <p className="text-xs text-gray-500 mt-1">
        For something the catalog doesn&apos;t have — a party tray, a bespoke box.
      </p>
    </div>
  );
};

const ItemRow = ({ item, index, products, items, onPatch, onLink, onRemove }) => {
  const match = item.is_custom_item && item.product_name.trim()
    ? products.find((p) => sameName(p.name, item.product_name) && !items.some((i) => i.product_id === p.id))
    : null;
  return (
    <div
      className="px-3 py-3 space-y-2 sm:space-y-0 sm:grid sm:grid-cols-12 sm:gap-2 sm:items-center"
      data-testid={`custom-order-item-row-${index}`}
    >
      <div className="sm:col-span-4 min-w-0">
        {item.is_custom_item ? (
          <>
            <Input
              value={item.product_name}
              onChange={(e) => onPatch(item._uid, { product_name: e.target.value })}
              placeholder="e.g. Pink birthday tray, 24 pieces"
              className="h-8 text-sm"
              data-testid={`custom-order-item-name-${index}`}
            />
            <span
              className="mt-1 inline-block text-[10px] uppercase tracking-wide text-gray-400"
              data-testid={`custom-order-item-oneoff-${index}`}
            >
              one-off
            </span>
            {match && (
              <p className="text-xs text-amber-700 mt-1">
                &ldquo;{item.product_name.trim()}&rdquo; is in the catalog.{' '}
                <button
                  type="button"
                  className="underline font-medium hover:text-amber-800"
                  onClick={() => onLink(item._uid, match)}
                  data-testid={`custom-order-item-link-${index}`}
                >
                  Use {match.name} instead
                </button>{' '}
                so it counts with the rest.
              </p>
            )}
          </>
        ) : (
          <>
            <p className="text-sm font-medium">{item.product_name}</p>
            {item.pack_size && <p className="text-xs text-gray-500">{item.pack_size}</p>}
          </>
        )}
      </div>

      <div className="sm:col-span-3 flex items-center gap-1">
        <span className="text-xs text-gray-400 w-10 sm:hidden">Qty</span>
        <button
          type="button"
          className="p-1.5 rounded hover:bg-gray-100"
          onClick={() => onPatch(item._uid, { quantity: item.quantity - 1 })}
          data-testid={`custom-order-item-minus-${index}`}
        >
          <Minus className="h-3 w-3" />
        </button>
        <Input
          type="number"
          min={1}
          value={item.quantity}
          onChange={(e) => onPatch(item._uid, { quantity: e.target.value })}
          className="w-16 h-8 text-center text-sm"
          data-testid={`custom-order-item-qty-${index}`}
        />
        <button
          type="button"
          className="p-1.5 rounded hover:bg-gray-100"
          onClick={() => onPatch(item._uid, { quantity: item.quantity + 1 })}
          data-testid={`custom-order-item-plus-${index}`}
        >
          <Plus className="h-3 w-3" />
        </button>
      </div>

      <div className="sm:col-span-2 flex items-center gap-1">
        <span className="text-xs text-gray-400 w-10 sm:hidden">Price</span>
        <Input
          type="number"
          step="0.01"
          min={0}
          value={item.unit_price}
          onChange={(e) => onPatch(item._uid, { unit_price: e.target.value })}
          className="w-full h-8 text-center text-sm"
          data-testid={`custom-order-item-price-${index}`}
        />
      </div>

      <div className="sm:col-span-2 flex items-center justify-between sm:justify-end">
        <span className="text-xs text-gray-400 sm:hidden">Subtotal</span>
        <span className="text-sm font-medium" data-testid={`custom-order-item-subtotal-${index}`}>
          ${(item.subtotal || 0).toFixed(2)}
        </span>
      </div>

      <div className="sm:col-span-1 text-right">
        <button
          type="button"
          onClick={() => onRemove(item._uid)}
          className="text-red-500 hover:text-red-700 p-2"
          data-testid={`custom-order-item-remove-${index}`}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export const CustomOrderItems = ({ items, products, total, onPatch, onLink, onRemove }) => {
  if (items.length === 0) {
    return (
      <p className="text-sm text-gray-500" data-testid="custom-order-no-items">
        Nothing on this order yet — click a product above, or add a one-off item.
      </p>
    );
  }
  return (
    <div>
      <Label className="text-base font-semibold">Order Items</Label>
      <div className="border rounded-lg mt-2 divide-y" data-testid="custom-order-items">
        <div className="hidden sm:grid grid-cols-12 gap-2 px-3 py-2 bg-gray-50 text-xs font-medium text-gray-500 uppercase">
          <div className="col-span-4">Product</div>
          <div className="col-span-3 text-center">Qty</div>
          <div className="col-span-2 text-center">Price</div>
          <div className="col-span-2 text-right">Subtotal</div>
          <div className="col-span-1"></div>
        </div>
        {items.map((item, index) => (
          <ItemRow
            key={item._uid}
            item={item}
            index={index}
            products={products}
            items={items}
            onPatch={onPatch}
            onLink={onLink}
            onRemove={onRemove}
          />
        ))}
        <div className="px-3 py-2 bg-gray-50 flex justify-between items-center">
          <span className="text-sm font-semibold">Total</span>
          <span className="text-lg font-bold text-amber-700" data-testid="custom-order-total">
            ${total.toFixed(2)}
          </span>
        </div>
      </div>
    </div>
  );
};
